import React, { useId, useState } from 'react';
import { X } from 'lucide-react';

/** Underlined tab strip. Controlled: pass `value` + `onChange`. */
export const Tabs = ({ tabs = [], value, onChange, className = '' }) => (
    <div className={`tabs ${className}`} role="tablist">
        {tabs.map((tab) => {
            const key = tab.value ?? tab;
            const label = tab.label ?? tab;
            const active = key === value;
            return (
                <button
                    key={key}
                    role="tab"
                    type="button"
                    aria-selected={active}
                    onClick={() => onChange(key)}
                    className={`tab ${active ? 'tab-active' : ''}`}
                >
                    {label}
                    {tab.count !== undefined && (
                        <span
                            className={`ml-1.5 px-1.5 rounded-sm text-micro tabular ${active ? 'bg-primary-50 text-primary-600' : 'bg-ink-100 text-ink-500'
                                }`}
                        >
                            {tab.count}
                        </span>
                    )}
                </button>
            );
        })}
    </div>
);

/** Compact view switcher (Month / Week / Day, Grid / List…). */
export const SegmentedControl = ({ options = [], value, onChange, size = 'md', label, className = '' }) => (
    <div
        role="group"
        aria-label={label}
        className={`inline-flex items-center gap-0.5 p-0.5 bg-ink-100 rounded-md ${className}`}
    >
        {options.map((opt) => {
            const key = opt.value ?? opt;
            const active = key === value;
            const Icon = opt.icon;
            return (
                <button
                    key={key}
                    type="button"
                    onClick={() => onChange(key)}
                    aria-pressed={active}
                    title={opt.title || opt.label}
                    className={`inline-flex items-center gap-1.5 rounded-sm font-medium transition-colors duration-150 ${size === 'sm' ? 'h-[26px] px-2 text-micro' : 'h-[30px] px-2.5 text-label'
                        } ${active
                            ? 'bg-white text-ink-900 shadow-xs'
                            : 'text-ink-500 hover:text-ink-800'
                        }`}
                >
                    {Icon && <Icon size={14} aria-hidden="true" />}
                    {opt.label ?? opt}
                </button>
            );
        })}
    </div>
);

/** CSS-only tooltip: no library, no layout thrash, respects reduced motion. */
export const Tooltip = ({ content, children, side = 'top', className = '' }) => {
    const id = useId();
    if (!content) return children;
    const pos = {
        top: 'bottom-full left-1/2 -translate-x-1/2 mb-1.5',
        bottom: 'top-full left-1/2 -translate-x-1/2 mt-1.5',
        left: 'right-full top-1/2 -translate-y-1/2 mr-1.5',
        right: 'left-full top-1/2 -translate-y-1/2 ml-1.5',
    };
    return (
        <span className={`relative inline-flex group ${className}`}>
            {React.isValidElement(children)
                ? React.cloneElement(children, { 'aria-describedby': id })
                : children}
            <span
                id={id}
                role="tooltip"
                className={`pointer-events-none absolute z-dropdown whitespace-nowrap rounded-sm bg-ink-900 px-2 py-1 text-micro font-medium text-white opacity-0 shadow-md transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100 ${pos[side]}`}
            >
                {content}
            </span>
        </span>
    );
};

/** Progress meter with a semantic colour ramp. */
export const ProgressBar = ({ value = 0, tone, showValue = false, className = '' }) => {
    const pct = Math.max(0, Math.min(100, Number(value) || 0));
    const color =
        tone ||
        (pct >= 100 ? 'var(--success)' : pct >= 60 ? 'var(--accent)' : pct >= 30 ? 'var(--warning)' : 'var(--danger)');
    return (
        <div className={`flex items-center gap-2 ${className}`}>
            <div
                className="progress-track"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
            >
                <div
                    className="progress-bar"
                    style={{ transform: `scaleX(${pct / 100})`, backgroundColor: color }}
                />
            </div>
            {showValue && (
                <span className="text-label text-ink-600 tabular w-9 text-right shrink-0">{pct}%</span>
            )}
        </div>
    );
};

/** Slide-over panel for secondary flows that should not steal the whole screen. */
export const Drawer = ({ isOpen, onClose, title, children, footer, width = 420 }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-modal" role="presentation">
            <div
                className="absolute inset-0 animate-fade-in"
                style={{ background: 'var(--bg-overlay)' }}
                onClick={onClose}
                aria-hidden="true"
            />
            <aside
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="absolute right-0 top-0 h-full bg-white shadow-lg flex flex-col max-w-full animate-fade-in-up"
                style={{ width }}
            >
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--divider-color)]">
                    <h2 className="text-lg font-semibold text-ink-900">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close panel"
                        className="p-1.5 rounded text-ink-500 hover:bg-ink-100 hover:text-ink-800 transition-colors duration-150"
                    >
                        <X size={17} aria-hidden="true" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
                {footer && (
                    <div className="px-5 py-3 border-t border-[var(--divider-color)] bg-[var(--bg-subtle)] flex justify-end gap-2">
                        {footer}
                    </div>
                )}
            </aside>
        </div>
    );
};

/** Small labelled key/value row used in detail panes. */
export const DetailRow = ({ label, children, className = '' }) => (
    <div className={`flex items-start gap-3 py-2 ${className}`}>
        <span className="text-label text-ink-500 w-32 shrink-0">{label}</span>
        <div className="text-base text-ink-800 min-w-0 flex-1">{children ?? <span className="text-muted">—</span>}</div>
    </div>
);

export const useDisclosure = (initial = false) => {
    const [isOpen, setIsOpen] = useState(initial);
    return {
        isOpen,
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        toggle: () => setIsOpen((v) => !v),
        setIsOpen,
    };
};
